import React, { useContext, useRef, useEffect, useMemo } from 'react';
import './PortfolioSummaryChart.scss';
import AppContext from 'app/AppContext';
import {
    useSelectCombineSellsAndShorts,
    useShowBuysAsPositive,
} from 'app/store/appSettings/selectors';
import { scaleLinear, scalePoint } from 'd3-scale';
import { extentLinear } from '@d3fc/d3fc-extent';

import { chartCartesian } from '@d3fc/d3fc-chart';
import { select } from 'd3-selection';
import { portfolioSummaryChartSeries } from './portfolioSummaryChartSeries';

import styleConstants from './variables.module.scss';
import { renderXAxis } from './xAxis';
import classNames from 'classnames';
import { computeChartData } from './data/portfolioSummaryChartData';
import { PortfolioSummaryConfigurationState } from '../store/instances/configuration/PortfolioSummaryConfigurationState';
import { PortfolioSummaryChartGroupData } from './data/portfolioSummaryChartBarData';
import { SeriesPoint } from 'utils/stackedSeries';
import { BarMouseOver } from './BarMouseOver';
import PortfolioSummaryChartLegends from './legends/PortfolioSummaryChartLegends';
import { buildYAxisContext } from './yAxisContext';
import { renderYAxis } from './yAxis';
import { PortfolioSummaryChartYAxisTooltip } from './portfolioSummaryChartYAxisTooltip';
import { YAxisMouseOver } from './YAxisMouseOver';
import { ensureXAxisLabelsDoNotOverlap } from 'utils/d3fc/xAxis';
import { useSelectPortfolioSummaryAggregatedData } from '../store/data/selectors';

export interface PortfolioSummaryChartProps {
    config: PortfolioSummaryConfigurationState;
    height: number;
}

export default function PortfolioSummaryChart(
    props: PortfolioSummaryChartProps
) {
    const { config, height } = props;
    const { aggregationField, quantities, sideNet } = config;

    const data = useSelectPortfolioSummaryAggregatedData(aggregationField);

    const { currencyFormat, numberFormat } = useContext(AppContext);
    const showBuysAsPositive = useShowBuysAsPositive();
    const combineSellsAndShorts = useSelectCombineSellsAndShorts();

    const nodeRef = useRef<HTMLDivElement>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const chartRef = useRef<any>();

    const xScaleRef = useRef(scaleLinear());
    const yScaleRef = useRef(scalePoint());

    const plotAreaHeight = height - parseInt(styleConstants.xAxisHeight);

    const mouseOverRef = useRef(new BarMouseOver());
    const yAxisMouseOverRef = useRef(new YAxisMouseOver());

    const xExtent = extentLinear()
        .accessors([
            () => 0,
            (a: SeriesPoint<PortfolioSummaryChartGroupData>[]) => {
                return a.map(
                    (d: SeriesPoint<PortfolioSummaryChartGroupData>) => {
                        return d[1];
                    }
                );
            },
        ])
        .pad([0, 0.1]);

    useEffect(() => {
        chartRef.current = chartCartesian(xScaleRef.current, yScaleRef.current)
            .yOrient('left')
            .xTickSizeOuter(0)
            .yTickSizeOuter(0);
    }, []);

    const { chartData, dimensions } = useMemo(
        () =>
            computeChartData(
                aggregationField,
                currencyFormat.exchangeRate ?? 1,
                data,
                showBuysAsPositive,
                plotAreaHeight,
                quantities,
                sideNet,
                combineSellsAndShorts
            ),
        [
            aggregationField,
            currencyFormat.exchangeRate,
            data,
            showBuysAsPositive,
            plotAreaHeight,
            quantities,
            sideNet,
            combineSellsAndShorts,
        ]
    );

    const chartSeries = useMemo(
        () =>
            portfolioSummaryChartSeries(
                dimensions.bandWidth,
                chartData,
                mouseOverRef.current,
                sideNet
            ),
        [chartData, dimensions.bandWidth, sideNet]
    );

    useEffect(() => {
        const node = nodeRef.current!;
        const chart = chartRef.current!;

        if (chartData.minX === 0 && chartData.maxX === 0) {
            xScaleRef.current.domain([0, 1200000000]);
            yScaleRef.current.domain([]);
        } else {
            const leftPad = chartData.minX < 0 ? 0.1 : 0;
            xExtent.pad([leftPad, 0.1]);

            if (sideNet === 'side') {
                xScaleRef.current.domain(
                    xExtent([...chartData.buy, ...chartData.sell])
                );
            } else {
                xScaleRef.current.domain(xExtent([...chartData.net]));
            }
            yScaleRef.current.domain(chartData.groupNames).padding(0.5);
        }

        chart.svgPlotArea(chartSeries);

        select(node).datum(chartData).call(chart);

        renderXAxis(
            numberFormat,
            chartRef.current,
            chartData.maxX,
            chartData.minX
        );

        ensureXAxisLabelsDoNotOverlap(chartRef.current);

        const yAxisContext = buildYAxisContext(
            chartData.groupNames,
            dimensions.actualGroupHeight
        );

        nodeRef.current &&
            renderYAxis(
                chartRef.current,
                nodeRef.current,
                yAxisContext,
                yAxisMouseOverRef.current
            );
    }, [
        chartData,
        chartSeries,
        data,
        xExtent,
        numberFormat,
        sideNet,
        dimensions,
    ]);

    const noData = chartData.maxX === 0 && chartData.minX === 0;
    return (
        <div
            className={classNames(
                'PortfolioSummaryChart',
                noData && 'no-data',
                'show-' + sideNet
            )}
        >
            <div ref={nodeRef} className="Chart" />
            <PortfolioSummaryChartYAxisTooltip
                yAxisMouseOver={yAxisMouseOverRef.current}
            />
            <PortfolioSummaryChartLegends
                chartHeight={plotAreaHeight}
                config={config}
                data={data}
                mouseOver={mouseOverRef.current}
            />
        </div>
    );
}
